'use client';

import Image from "next/image";
import Link from "next/link";
import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BlogDetail } from "@/types/blogs";
import { ArrowLeft, Calendar, ArrowRight } from "lucide-react";
import { capitalize, formatDate } from "@/lib/utils";
import CallToActionBlock from "@/components/marketing/callToActionBlock";
import { storeLinks } from "@/components/sections/app-download";

function getYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function embedYouTubeLinks(html: string): string {
  return html.replace(
    /<a\s[^>]*href=["']([^"']*(?:youtube\.com\/watch|youtu\.be\/)[^"']*)["'][^>]*>[\s\S]*?<\/a>/gi,
    (match, url) => {
      const videoId = getYouTubeId(url);
      if (!videoId) return match;
      return `<div class="yt-embed"><iframe src="https://www.youtube.com/embed/${videoId}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy"></iframe></div>`;
    }
  );
}

function stripFirstH1(html: string): string {
  return html.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '');
}

function RelatedCard({ post }: { post: BlogDetail["relatedPosts"][number] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="h-full"
    >
      <Link
        href={`/blog/${post.slug}`}
        className="group flex flex-col h-full global-border-radius bg-brand-white-light border border-brand-grey/25 hover:shadow-md hover:shadow-black/5 transition-all duration-200 overflow-hidden"
      >
        <div className="relative w-full aspect-[16/9] overflow-hidden">
          <img
            src={post.featured_image}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        <div className="flex items-center gap-4 p-5">
          <p className="text-brand-black text-sm lg:text-base 3xl:text-lg font-semibold leading-snug group-hover:text-brand-teal transition-colors duration-200 line-clamp-2">
            {post.title}
          </p>
          <ArrowRight className="h-4 w-4 text-brand-muted flex-shrink-0 transition-colors duration-200 group-hover:text-brand-teal" />
        </div>
      </Link>
    </motion.div>
  );
}

function RelatedPostsSlider({ posts }: { posts: BlogDetail["relatedPosts"] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);

  const updateArrowState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth + 4;
    setNeedsScroll(overflowing);
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    updateArrowState();
    window.addEventListener("resize", updateArrowState);
    return () => window.removeEventListener("resize", updateArrowState);
  }, [posts]);

  const scrollByCard = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector<HTMLElement>("[data-card]")?.offsetWidth ?? 320;
    el.scrollBy({
      left: direction === "left" ? -(cardWidth + 20) : cardWidth + 20,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-col lg:flex-row gap-2 lg:gap-12 xl:mt-22 md:mt-16 sm:mt-0 mb-12 lg:mb-16">
      {/* Left panel */}
      <div className="lg:w-[175px] 2xl:w-[260px] shrink-0 lg:ml-10">
        <h2 className="text-3xl lg:text-4xl 2xl:text-6xl font-bold 2xl:leading-15 text-brand-black mb-5">Related Articles</h2>
        {needsScroll && (
          <div className="flex items-center gap-3 max-lg:hidden mt-12">
            <button
              type="button"
              onClick={() => scrollByCard("left")}
              disabled={!canScrollLeft}
              aria-label="Previous"
              className="flex h-12 w-12 items-center justify-center global-border-radius  global-border-radius text-slate-800 bg-brand-grey/15 border border-stroke/60 hover:bg-brand-grey/30 transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard("right")}
              disabled={!canScrollRight}
              aria-label="Next"
              className="flex  h-12 w-12 items-center justify-center global-border-radius text-slate-800 bg-brand-grey/15 border border-stroke/60 hover:bg-brand-grey/30 transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ArrowRight className="h-6 w-6" />
            </button>
          </div>
        )}
      </div>

      {/* Right: scrollable cards */}
      <div className="relative flex-1 min-w-0">
        <div
          ref={scrollRef}
          onScroll={updateArrowState}
          className="flex gap-5 overflow-x-auto overflow-y-hidden pb-4 snap-x scroll-smooth
                     [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
                     [mask-image:linear-gradient(to_right,black_92%,transparent_100%)]
                     [-webkit-mask-image:linear-gradient(to_right,black_92%,transparent_100%)]"
        >
          {posts.map((post) => (
            <div
              key={post.slug}
              data-card
              className="snap-start shrink-0 w-[295px] sm:w-[350px] lg:w-[425px] 3xl:w-[500px]"
            >
              <RelatedCard post={post} />
            </div>
          ))}
        </div>
      </div>
          {needsScroll && (
          <div className="flex justify-center items-center gap-3 mt-4 lg:hidden">
            <button
              type="button"
              onClick={() => scrollByCard("left")}
              disabled={!canScrollLeft}
              aria-label="Previous"
              className="flex h-12 w-12 items-center justify-center global-border-radius text-slate-800 bg-brand-grey/15 border border-stroke/60 hover:bg-brand-grey/30 transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard("right")}
              disabled={!canScrollRight}
              aria-label="Next"
              className="flex h-12 w-12 items-center justify-center global-border-radius text-slate-800 bg-brand-grey/15 border border-stroke/60 hover:bg-brand-grey/30 transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ArrowRight className="h-6 w-6" />
            </button>
          </div>
        )}
    </div>
  );
}

export default function BlogPostContent({ blog }: { blog: BlogDetail }) {
  const hasHero = !!blog.featured_image;

  return (
    <div className="min-h-screen bg-brand-text">

      <div className="mx-auto">
        <div className="relative sm:h-[60vh] min-h-[285px] max-h-[480px] xl:max-h-[650px] max-w-[1800px] mx-auto overflow-hidden">
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="absolute top-0 inset-x-0 z-20 pt-4 sm:pt-8 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto px-4 lg:px-0" 
          >
                  
            <div className="flex items-center gap-2 text-sm 3xl:text-base">
                <Link
                  href="/blog"
                  className="inline-flex items-center gap-2 text-sm text-white 3xl:text-base font-medium hover:text-brand-green transition-colors duration-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  All articles
                </Link>
                <span className="text-brand-stroke max-sm:hidden text-white">/</span>
                <span className="max-sm:hidden text-white">{blog.title}</span>
            </div>
            
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.45 }}
          >
            {/* ── Featured image ── */}
            {blog.featured_image && (
          
                <div className="w-full absolute inset-0">
                  <div
                    className="relative mx-auto w-full h-full overflow-hidden bg-cover bg-center"
                    style={{ backgroundImage: `url(${blog.featured_image})` }}
                    role="img"
                    aria-label={blog.title}
                  />

                  <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/80 via-black/20 to-transparent pointer-events-none" />
                </div>
           
            )}
          </motion.div>
        </div>
        
        <div className="mx-auto mt-6 sm:mt-12">      
          <div className="flex flex-col gap-4 pb-6 md:pb-12 lg:pb-20">
            <div className="w-full max-w-[950px] mx-auto px-4 lg:px-0 overflow-hidden">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.15 }}
              >
                
                {/* Title */}
                <h1
                  className="text-2xl sm:text-4xl lg:text-5xl xl:text-[3rem] font-bold text-brand-card leading-[1.15] tracking-tight mb-5"
                  style={{ textWrap: 'inherit' } as React.CSSProperties}
                >
                  {blog.title}
                </h1>

                {/* Category + date row */}
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  {blog.category && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-sm 3xl:text-base font-semibold text-brand-teal-light">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-teal-light" />
                      {capitalize(blog.category)}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 text-sm 3xl:text-base text-brand-muted-light">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(blog.published_at)}
                    {blog.author && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>{blog.author.name}</span>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* ── Article content ── */}
              <motion.article
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="min-w-0"
              >
                <div
                  className="
                    [&_h1]:text-2xl [&_h1]:text-brand-card sm:[&_h1]:text-3xl lg:[&_h1]:text-4xl 3xl:[&_h1]:text-5xl [&_h1]:font-bold [&_h1]:text-brand-text [&_h1]:leading-tight [&_h1]:mb-6 [&_h1]:mt-0
                    [&_h2]:text-xl sm:[&_h2]:text-2xl lg:[&_h2]:text-3xl 3xl:[&_h2]:text-4xl [&_h2]:font-bold [&_h2]:text-brand-card [&_h2]:leading-snug [&_h2]:mt-10 [&_h2]:mb-4
                    [&_h3]:text-lg sm:[&_h3]:text-xl 3xl:[&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-brand-card [&_h3]:leading-snug [&_h3]:mt-8 [&_h3]:mb-3
                    [&_h4]:text-base sm:[&_h4]:text-lg 3xl:[&_h4]:text-xl [&_h4]:font-semibold [&_h4]:text-brand-card [&_h4]:mt-6 [&_h4]:mb-2
                    [&_p]:text-brand-card [&_p]:leading-[1.85] [&_p]:mb-5 [&_p]:text-sm sm:[&_p]:text-base 3xl:[&_p]:text-lg
                    
                    [&_p:has(>a:only-child)]:px-0 [&_p:has(>a:only-child)]:py-2

                    [&_p:has(>a:only-child)_a]:font-semibold [&_p:has(>a:only-child)_a]:text-smsm:[&_p:has(>a:only-child)_a]:text-base 
                    [&_p:has(>a:only-child)_a]:text-brand-teal [&_p:has(>a:only-child)_a]:no-underline
                    [&_p:has(>a:only-child)_a]:border-b [&_p:has(>a:only-child)_a]:border-brand-teal [&_p:has(>a:only-child)_a:hover]:border-brand-dark-grey
                    [&_p:has(>a:only-child)_a]:transition-colors [&_p:has(>a:only-child)_a:hover]:text-brand-dark-grey
                    [&_p:has(>a:only-child)_a]:pb-[3px]

                    [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:text-brand-text/80 [&_ul]:list-disc [&_ul]:space-y-2
                    [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:text-brand-text/80 [&_ol]:list-decimal [&_ol]:space-y-2
                    [&_ul_ul]:pl-5 [&_ul_ul]:mt-2 [&_ul_ul]:mb-0 [&_ol_ol]:pl-5 [&_ol_ol]:mt-2 [&_ol_ol]:mb-0
                    [&_li]:text-sm [&_li]:text-brand-card sm:[&_li]:text-base 3xl:[&_li]:text-lg [&_li]:leading-relaxed
                    [&_li::marker]:text-base-muted-light
                    [&_li_li::marker]:text-brand-muted-light
                    [&_a]:text-brand-teal [&_a]:underline [&_a]:underline-offset-2 [&_a]:font-medium
                    [&_strong]:font-semibold [&_strong]:text-brand-card
                    [&_em]:italic [&_em]:text-brand-muted-light
                    [&_blockquote]:border-l-4 [&_blockquote]:border-brand-teal/40 [&_blockquote]:pl-5 [&_blockquote]:py-1 [&_blockquote]:my-6 [&_blockquote]:text-brand-muted-light [&_blockquote]:italic [&_blockquote]:text-sm sm:[&_blockquote]:text-base
                    [&_hr]:border-brand-stroke/50 [&_hr]:my-8
                    [&_code]:bg-brand-surface [&_code]:text-brand-card [&_code]:text-xs sm:[&_code]:text-sm [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono
                    
                    [&_ul:has(a)]:list-none [&_ul:has(a)]:m-0 [&_ul:has(a)]:mb-10 [&_ul:has(a)]:p-0

                    [&_ul:has(a)]:list-none [&_ul:has(a)]:m-0 [&_ul:has(a)]:mb-10 [&_ul:has(a)]:p-0
                    [&_ul:has(a)]:text-brand-teal [&_ul:has(a)_li_a]:no-underline [&_ul:has(a)_li_a]:font-semibold
                    [&_ul:has(a)_li_a]:border-b [&_ul:has(a)_li_a]:border-brand-teal [&_ul:has(a)_li_a:hover]:border-brand-dark-grey
                    [&_ul:has(a)_li_a]:pb-[3px] [&_ul:has(a)_li_a:hover]:text-brand-dark-grey

                    [&_table]:border-separate [&_table]:border-spacing-0 [&_table]:overflow-hidden
                    [&_table]:global-border-radius [&_table]:border [&_table]:border-brand-teal/15 [&_table]:shadow-sm [&_table]:text-sm sm:[&_table]:text-base

                    [&_thead]:bg-brand-teal/10 
                    [&_th]:px-6 [&_th]:py-4 [&_th]:text-left sm:[_th]:text-base [&_th]:text-sm [&_th]:font-bold 
                    [&_th]:text-[#00696B] [&_th]:uppercase [&_th]:tracking-wide 
                    [&_th]:border-b [&_th]:border-brand-teal/15

                    [&_td]:px-6 [&_td]:py-4 [&_td]:text-slate-700 
                    [&_td]:border-b [&_td]:border-brand-teal/10 [&_td]:align-top [&_td]:leading-relaxed

                    [&_tr:last-child_td]:border-b-0
                    [&_tbody_tr]:bg-white

                    [&_.yt-embed]:relative [&_.yt-embed]:w-full [&_.yt-embed]:pb-[56.25%] [&_.yt-embed]:my-6 [&_.yt-embed]:global-border-radius [&_.yt-embed]:overflow-hidden [&_.yt-embed]:bg-black
                    [&_.yt-embed_iframe]:absolute [&_.yt-embed_iframe]:inset-0 [&_.yt-embed_iframe]:w-full [&_.yt-embed_iframe]:h-full
                  "
                  dangerouslySetInnerHTML={{
                    __html: wrapTables(embedYouTubeLinks(hasHero ? stripFirstH1(blog.content) : blog.content)),
                  }}
                />
              </motion.article>
              </div>
                  
              {/*<motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.75 }}
              >
                <aside className="max-w-[300px] mx-auto">
                  <div className="text-center global-border-radius bg-brand-grey/15 p-6 border border-brand-grey/20 shadow-lg text-center">
                    <p className="max-sm:hidden font-bold text-base text-slate-800 mb-1">Scan & download</p>
                    <p className="sm:hidden font-bold text-base text-slate-800 mb-1">Download the app</p>
                    <p className="text-base text-slate-700 mb-3">Vell App</p>
                    <div className="max-sm:hidden relative mx-auto aspect-square w-full max-w-[150px] overflow-hidden global-border-radius bg-brand-card p-2">
                      <Image
                        src="/QR-Download.png"
                        alt="QR code to download the Vell app"
                        fill
                        className="object-contain"
                        sizes="(max-width: 768px) 120px, 140px"
                        priority
                        fetchPriority="high"
                      />
                    </div>
                    <div className="sm:hidden">
                      <div className="flex flex-wrap items-center mb-4">
                        {storeLinks.map((link) => (
                          <Link
                            key={link.name}
                            href={link.href}
                            className="group inline-flex items-center justify-center global-border-radius w-full px-2 py-1 transition hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                            aria-label={`Open ${link.name}`}
                          >
                            <Image
                              src={link.src}
                              alt={link.name}
                              width={175}
                              height={45}
                              className="object-contain"
                              sizes="(max-width: 640px) 160px, 180px"
                              priority={link.name === "App Store"}
                            />
                          </Link>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 mt-4">
                      iOS · Android · Huawei AppGallery<span className="max-sm:hidden">. One scan to install and start.</span>
                    </p>
                  </div>
                </aside>
              </motion.div>*/}
             
            </div>

            {/* ── Related posts ── */}
            {blog.relatedPosts?.length > 0 && (
              <section className="px-4 overflow-hidden">
                <div className="md:border-t md:border-brand-stroke/30">
                  <RelatedPostsSlider posts={blog.relatedPosts} />
                </div>
              </section>
            )}

            {/* ── CTA ── */}
            <div className="max-w-4xl mx-auto">
              <CallToActionBlock
                smallText="Explore Vell"
                title="See what Vell can do for you"
                subtitle="International payments and travel eSIM — built for people who move globally."
                buttonText="Get started"
                href="/register"
                glow="teal"
              />
            </div>
          </div>
        </div>
    </div>
  );
}

function wrapTables(html: string) {
  return html
    .replace(/<table/g, '<div class="table-scroll"><table')
    .replace(/<\/table>/g, "</table></div>");
}